import {
  detectVersionType,
  getBaseName,
  getDisplayBaseName,
  getSimilarity,
  isStemsArchive,
} from '@utils/fileGrouping';
import { UploadFileGroup, UploadVersionType } from '../types';

const SIMILARITY_THRESHOLD = 0.75;
const STEMS_SIMILARITY_THRESHOLD = 0.7;

function selectMainFileName(
  fileNames: string[],
  versionTypes: Record<string, UploadVersionType>
): string {
  const DERIVED = new Set(['tagged', 'clean', 'stems']);
  const nonDerived = fileNames.filter((n) => !DERIVED.has(versionTypes[n] ?? ''));
  const candidates = nonDerived.length > 0 ? nonDerived : fileNames;

  const versionPriority: Record<string, number> = {
    master: 5,
    wav: 4,
    mp3: 2,
    stems: 1,
    tagged: 0,
    clean: 0,
  };

  return candidates.reduce((best, current) => {
    const bestScore = versionPriority[versionTypes[best] ?? ''] ?? 3;
    const currentScore = versionPriority[versionTypes[current] ?? ''] ?? 3;
    return currentScore > bestScore ? current : best;
  });
}

function createGroupFromFileNames(fileNames: string[]): UploadFileGroup {
  const versionTypes: Record<string, UploadVersionType> = {};
  fileNames.forEach((name) => {
    versionTypes[name] = detectVersionType(name) ?? null;
  });

  const mainFileName = selectMainFileName(fileNames, versionTypes);
  const baseName = getDisplayBaseName(mainFileName);

  return {
    id: crypto.randomUUID(),
    baseName,
    fileNames,
    mainFileName,
    versionTypes,
    bpm: '',
    musicalKey: '',
    tags: [],
  };
}

/**
 * Groups local File objects by name similarity (mirrors cloud explorer logic).
 */
export function groupLocalFiles(files: File[]): UploadFileGroup[] {
  if (files.length === 0) return [];

  const fileNames = files.map((f) => f.name);
  const nameToFile = new Map(files.map((f) => [f.name, f]));

  const baseNames = new Map<string, string>();
  fileNames.forEach((name) => {
    baseNames.set(name, getBaseName(name));
  });

  const similarityGraph = new Map<string, Set<string>>();
  fileNames.forEach((name) => similarityGraph.set(name, new Set()));

  for (let i = 0; i < fileNames.length; i++) {
    const name1 = fileNames[i];
    const base1 = baseNames.get(name1)!;
    const isStems1 =
      detectVersionType(name1) === 'stems' || isStemsArchive(name1);

    for (let j = i + 1; j < fileNames.length; j++) {
      const name2 = fileNames[j];
      const base2 = baseNames.get(name2)!;
      const similarity = getSimilarity(base1, base2);
      const threshold = isStems1 ? STEMS_SIMILARITY_THRESHOLD : SIMILARITY_THRESHOLD;

      if (similarity > threshold) {
        similarityGraph.get(name1)!.add(name2);
        similarityGraph.get(name2)!.add(name1);
      }
    }
  }

  const visited = new Set<string>();
  const groups: UploadFileGroup[] = [];

  const findComponent = (start: string): string[] => {
    const component: string[] = [];
    const queue = [start];
    visited.add(start);

    while (queue.length > 0) {
      const current = queue.shift()!;
      component.push(current);
      const neighbors = similarityGraph.get(current) ?? new Set();
      neighbors.forEach((neighbor) => {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      });
    }
    return component;
  };

  fileNames.forEach((name) => {
    if (visited.has(name)) return;
    const component = findComponent(name);
    if (component.length > 0) {
      groups.push(createGroupFromFileNames(component));
    }
  });

  // Ensure every file is in a group (fallback singletons)
  const assigned = new Set(groups.flatMap((g) => g.fileNames));
  fileNames.forEach((name) => {
    if (!assigned.has(name) && nameToFile.has(name)) {
      groups.push(createGroupFromFileNames([name]));
    }
  });

  return groups;
}

/** Returns true when every file belongs to exactly one group. */
export function areAllFilesGrouped(files: File[], groups: UploadFileGroup[]): boolean {
  if (files.length === 0) return false;
  const assigned = new Set(groups.flatMap((g) => g.fileNames));
  return files.every((f) => assigned.has(f.name)) && assigned.size === files.length;
}

/** Sync groups after file list changes — keeps group metadata where possible. */
export function syncGroupsWithFiles(
  files: File[],
  existingGroups: UploadFileGroup[]
): UploadFileGroup[] {
  const fileNameSet = new Set(files.map((f) => f.name));
  const preserved: UploadFileGroup[] = [];

  existingGroups.forEach((group) => {
    const remaining = group.fileNames.filter((n) => fileNameSet.has(n));
    if (remaining.length === 0) return;

    const versionTypes: Record<string, UploadVersionType> = {};
    remaining.forEach((name) => {
      versionTypes[name] = group.versionTypes[name] ?? detectVersionType(name) ?? null;
    });

    const mainFileName = remaining.includes(group.mainFileName)
      ? group.mainFileName
      : selectMainFileName(remaining, versionTypes);

    preserved.push({
      ...group,
      fileNames: remaining,
      mainFileName,
      versionTypes,
      baseName: getDisplayBaseName(mainFileName),
    });
  });

  const assigned = new Set(preserved.flatMap((g) => g.fileNames));
  const unassigned = files.filter((f) => !assigned.has(f.name));

  if (unassigned.length === 0) return preserved;

  const newGroups = groupLocalFiles(unassigned);
  return [...preserved, ...newGroups];
}
